import { Feature, Rule, Ruleset, RuleType } from 'entities/campaign';
import * as Rules from 'entities/campaign/rules';
import { getReadableLabel } from 'entities/campaign/rules/Geolocation';
import { getOptionalRuleTypes } from 'entities/campaign/rulesets/mandatory-and-optional-rules';
import { Forms, OwnProps } from '../ruleForms';
import { TFunction } from 'react-i18next';

export interface RuleConfig {
  title: string;
  glyph: string;
  form?: React.ComponentClass<OwnProps>;
  getValue: (rule: Rule) => string | null;
  valid: (rule: Rule, rules?: Rule[]) => boolean;
}

function millisecondsToMinutes(milliseconds: number): number {
  return milliseconds / 1000 / 60;
}

const RuleConfigs: { [ruleType in RuleType]: (translator: TFunction<'campaigns'>) => RuleConfig } = {
  '.BrowseAbandonment': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.browseAbandonment.title'),
    glyph: 'search',
    form: Forms.BrowseAbandonment,
    getValue: (rule: Rules.BrowseAbandonment): string =>
      translator('editor.rules.browseAbandonment.value', { duration: millisecondsToMinutes(rule.minSessionTime || 0) }),
    valid: (rule: Rules.BrowseAbandonment): boolean => rule.minSessionTime > 0,
  }),

  '.CartAbandonment': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.cartAbandonment.title'),
    glyph: 'cart-arrow-down',
    form: Forms.CartAbandonment,
    getValue: (): string => translator('editor.rules.cartAbandonment.value'),
    valid: (rule: Rules.CartAbandonment): boolean => rule.unsubscribeDefined,
  }),

  '.ActiveBasketContainsItemWithId': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.activeBasketContainsItemWithId.title'),
    glyph: 'shopping-cart',
    form: Forms.ActiveBasketContainsItemWithId,
    getValue: (rule: Rules.ActiveBasketContainsItemWithId): string =>
      rule.productItemIds && rule.productItemIds.length > 0
        ? translator('editor.rules.activeBasketContainsItemWithId.value', {
            amountOfProducts: rule.productItemIds.length,
          })
        : null,
    valid: (rule: Rules.ActiveBasketContainsItemWithId): boolean =>
      !!rule.productItemIds && rule.productItemIds.length > 0,
  }),
  '.ActiveBasketContainsItemWithName': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.activeBasketContainsItemWithName.title'),
    glyph: 'shopping-cart',
    form: Forms.ActiveBasketContainsItemWithName,
    getValue: (rule: Rules.ActiveBasketContainsItemWithName): string =>
      rule.productItemNames && rule.productItemNames.length > 0
        ? translator('editor.rules.activeBasketContainsItemWithId.value', {
            amountOfProducts: rule.productItemNames.length,
          })
        : null,
    valid: (rule: Rules.ActiveBasketContainsItemWithName): boolean =>
      !!rule.productItemNames && rule.productItemNames.length > 0,
  }),

  '.ActiveBasketValueIsAtLeast': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.activeBasketValueIsAtLeast.title'),
    glyph: 'shopping-cart',
    form: Forms.ActiveBasketValueIsAtLeast,
    getValue: (rule: Rules.ActiveBasketValueIsAtLeast): string =>
      rule.minValue
        ? translator('editor.rules.activeBasketValueIsAtLeast.value', { basketValue: rule.minValue.toFixed(2) })
        : null,
    valid: (rule: Rules.ActiveBasketValueIsAtLeast): boolean => !!rule.minValue,
  }),

  '.ActiveBasketValueIsLessThan': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.activeBasketValueIsLessThan.title'),
    glyph: 'shopping-cart',
    form: Forms.ActiveBasketValueIsLessThan,
    getValue: (rule: Rules.ActiveBasketValueIsLessThan): string =>
      rule.maxValue
        ? translator('editor.rules.activeBasketValueIsLessThan.value', { basketValue: rule.maxValue.toFixed(2) })
        : null,
    valid: (rule: Rules.ActiveBasketValueIsLessThan): boolean => !!rule.maxValue,
  }),

  '.DeviceType': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.deviceType.title'),
    glyph: 'mobile',
    form: Forms.DeviceType,
    getValue: (rule: Rules.DeviceType): string => rule.deviceType.valueOf(),
    valid: (rule: Rules.DeviceType): boolean => !!rule.deviceType,
  }),

  '.ExcludeEmailSource': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.excludeEmailSource.title'),
    glyph: 'address-book',
    form: Forms.ExcludeEmailSource,
    getValue: (rule: Rules.ExcludeEmailSource): string =>
      rule.excludedSources ? rule.excludedSources.join(', ') : null,
    valid: (rule: Rules.ExcludeEmailSource, rules?: Rule[]): boolean => {
      if (rules && rules.find(rule => rule.type === '.IncludeEmailSource')) {
        return false;
      }
      return rule.excludedSources && rule.excludedSources.length > 0;
    },
  }),

  '.IncludeEmailSource': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.includeEmailSource.title'),
    glyph: 'address-book',
    form: Forms.IncludeEmailSource,
    getValue: (rule: Rules.IncludeEmailSource): string =>
      rule.includedSources ? rule.includedSources.join(', ') : null,
    valid: (rule: Rules.IncludeEmailSource, rules?: Rule[]): boolean => {
      if (rules && rules.find(rule => rule.type === '.ExcludeEmailSource')) {
        return false;
      }
      return rule.includedSources && rule.includedSources.length > 0;
    },
  }),

  '.ImpressionPathWhitelist': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.impressionPathWhitelist.title'),
    glyph: 'link',
    form: Forms.ImpressionPathWhitelist,
    getValue: (rule: Rules.ImpressionPathWhitelist): string =>
      rule.whitelist && rule.whitelist.filter(item => item !== '').length > 0
        ? translator('editor.rules.impressionPathWhitelist.value', { numberOfUrls: rule.whitelist.length })
        : null,
    valid: (rule: Rules.ImpressionPathWhitelist): boolean =>
      rule.whitelist && rule.whitelist.filter(item => item !== '').length > 0,
  }),

  '.NoActiveMarketingPermission': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.noActiveMarketingPermission.title'),
    glyph: 'handshake-alt',
    form: Forms.NoActiveMarketingPermission,
    getValue: (rule: Rules.NoActiveEmailMarketingPermission): string =>
      rule.showAgainAfter !== undefined && rule.showAgainAfter !== null
        ? rule.showAgainAfter === 0
          ? translator('editor.rules.noActiveMarketingPermission.values.everyTime')
          : translator('editor.rules.noActiveMarketingPermission.values.periodically')
        : translator('editor.rules.noActiveMarketingPermission.values.once'),
    valid: (): boolean => true,
  }),

  '.NoActiveSMSMarketingPermission': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.noActiveSmsMarketingPermission.title'),
    glyph: 'handshake-alt',
    form: Forms.NoActiveMarketingPermission,
    getValue: (rule: Rules.NoActiveSMSMarketingPermission): string =>
      rule.showAgainAfter !== undefined && rule.showAgainAfter !== null
        ? rule.showAgainAfter === 0
          ? translator('editor.rules.noActiveSmsMarketingPermission.values.everyTime')
          : translator('editor.rules.noActiveSmsMarketingPermission.values.periodically')
        : translator('editor.rules.noActiveSmsMarketingPermission.values.once'),
    valid: (): boolean => true,
  }),

  '.Geolocation': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.geolocation.title'),
    glyph: 'globe-europe',
    form: Forms.Geolocation,
    getValue: (geolocation: Rules.Geolocation): string =>
      geolocation.location && getReadableLabel(geolocation.location, translator),
    valid: (geolocation: Rules.Geolocation): boolean => !!geolocation.location,
  }),

  '.UserLoggedIn': (translator: TFunction<'campaigns'>) => ({
    title: translator('editor.rules.userLoggedIn.title'),
    glyph: 'user',
    form: Forms.UserLoggedIn,
    getValue: (rule: Rules.UserLoggedIn): string => {
      if (rule.loggedIn === null || rule.loggedIn === undefined) {
        return '';
      }
      return rule.loggedIn
        ? translator('editor.rules.userLoggedIn.values.loggedIn')
        : translator('editor.rules.userLoggedIn.values.notLoggedIn');
    },
    valid: (rule: Rules.UserLoggedIn): boolean => rule.loggedIn !== undefined && rule.loggedIn !== null,
  }),
};

/** API returns dotted rule types (e.g. ".Always"); use as-is for config lookup. */
function ruleTypeKey(type: RuleType | string): string {
  return String(type ?? '');
}

export const getRuleConfig = (translator: TFunction<'campaigns'>, type: RuleType): RuleConfig => {
  const key = ruleTypeKey(type) as RuleType;
  const fn = RuleConfigs[key];
  if (typeof fn !== 'function') {
    return {
      title: key,
      glyph: 'question',
      getValue: () => null,
      valid: () => true,
    };
  }
  return fn(translator);
};

export const getUnusedRuleList = (translator: TFunction<'campaigns'>, feature: Feature, ruleset: Ruleset): string[] => {
  const usedRules = new Set((ruleset.rules ?? []).map((rule): string => ruleTypeKey(rule?.type ?? '')));

  return getOptionalRuleTypes(feature, ruleset.subFeature)
    .filter((rule): boolean => !usedRules.has(rule))
    .sort((a, b): number => (getRuleConfig(translator, a).title > getRuleConfig(translator, b).title ? 1 : -1));
};
